'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Code2, Server, Gauge, Star, ChevronDown, ChevronUp, Globe, GitBranch, Cpu, Database, Cloud, CheckCircle2, Clock, AlertTriangle, TrendingUp, Users, ExternalLink, MessageSquare, Zap, Activity, BarChart2, Package, Layers,  } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface TechBadge {
  name: string;
  category: 'frontend' | 'backend' | 'database' | 'devops' | 'cloud';
}

interface DeployEnv {
  name: string;
  url: string;
  status: 'live' | 'staging' | 'down' | 'maintenance';
  lastDeploy: string;
  version: string;
  uptime: string;
}

interface PerfMetric {
  label: string;
  value: string;
  score: number; // 0-100
  trend: 'up' | 'down' | 'stable';
}

interface ClientFeedback {
  author: string;
  company: string;
  role: string;
  rating: number;
  comment: string;
  date: string;
  avatar: string;
}

interface PortfolioProject {
  id: string;
  name: string;
  client: string;
  description: string;
  status: 'In Development' | 'Testing' | 'Deployed' | 'Maintenance' | 'Completed';
  category: string;
  startDate: string;
  launchDate: string;
  techStack: TechBadge[];
  deployEnvs: DeployEnv[];
  perfMetrics: PerfMetric[];
  feedback: ClientFeedback[];
  teamSize: number;
  budget: string;
  liveUrl?: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────

const portfolioProjects: PortfolioProject[] = [
  {
    id: 'pf-001',
    name: 'Nexus ERP Platform',
    client: 'Pinnacle Systems',
    description:
      'Enterprise resource planning platform covering inventory, procurement, HR, and financial reporting with real-time dashboards and multi-branch support.',
    status: 'Testing',
    category: 'ERP / CRM',
    startDate: 'Mar 2026',
    launchDate: 'Sep 2026',
    teamSize: 8,
    budget: '$84,000',
    techStack: [
      { name: 'Next.js 15', category: 'frontend' },
      { name: 'TypeScript', category: 'frontend' },
      { name: 'Tailwind CSS', category: 'frontend' },
      { name: 'Node.js', category: 'backend' },
      { name: 'Express', category: 'backend' },
      { name: 'PostgreSQL', category: 'database' },
      { name: 'Redis', category: 'database' },
      { name: 'Docker', category: 'devops' },
      { name: 'GitHub Actions', category: 'devops' },
      { name: 'AWS EC2', category: 'cloud' },
    ],
    deployEnvs: [
      { name: 'Production', url: 'nexus.pinnaclesys.com', status: 'maintenance', lastDeploy: '2 days ago', version: 'v0.9.1', uptime: '99.1%' },
      { name: 'Staging', url: 'staging.nexus.pinnaclesys.com', status: 'live', lastDeploy: '6 hours ago', version: 'v0.9.4', uptime: '98.7%' },
      { name: 'QA', url: 'qa.nexus.pinnaclesys.com', status: 'live', lastDeploy: '1 hour ago', version: 'v0.9.5-rc', uptime: '97.2%' },
    ],
    perfMetrics: [
      { label: 'Lighthouse Score', value: '87/100', score: 87, trend: 'up' },
      { label: 'Avg Load Time', value: '1.4s', score: 82, trend: 'up' },
      { label: 'API Response', value: '210ms', score: 78, trend: 'stable' },
      { label: 'Test Coverage', value: '74%', score: 74, trend: 'up' },
    ],
    feedback: [
      {
        author: 'James Whitfield',
        company: 'Pinnacle Systems',
        role: 'CTO',
        rating: 5,
        comment: 'The ERP has transformed our operations. Real-time inventory visibility alone saved us 12% in procurement costs within the first month of UAT.',
        date: 'Aug 2, 2026',
        avatar: 'JW',
      },
    ],
  },
  {
    id: 'pf-002',
    name: 'Halcyon Mobile App',
    client: 'Halcyon Ventures',
    description:
      'Cross-platform investment portfolio tracker with live market feeds, AI-driven insights, push notifications, and biometric authentication.',
    status: 'Deployed',
    category: 'Mobile App',
    startDate: 'Jan 2026',
    launchDate: 'Aug 2026',
    teamSize: 5,
    budget: '$52,000',
    liveUrl: 'https://halcyonapp.io',
    techStack: [
      { name: 'React Native', category: 'frontend' },
      { name: 'Expo', category: 'frontend' },
      { name: 'TypeScript', category: 'frontend' },
      { name: 'FastAPI', category: 'backend' },
      { name: 'Python', category: 'backend' },
      { name: 'MongoDB', category: 'database' },
      { name: 'Firebase', category: 'cloud' },
      { name: 'AWS Lambda', category: 'cloud' },
      { name: 'Bitrise CI', category: 'devops' },
    ],
    deployEnvs: [
      { name: 'App Store', url: 'apps.apple.com/halcyon', status: 'live', lastDeploy: '5 days ago', version: 'v2.1.0', uptime: '99.9%' },
      { name: 'Play Store', url: 'play.google.com/halcyon', status: 'live', lastDeploy: '5 days ago', version: 'v2.1.0', uptime: '99.9%' },
      { name: 'Beta Channel', url: 'beta.halcyonapp.io', status: 'staging', lastDeploy: '1 day ago', version: 'v2.2.0-beta', uptime: '98.4%' },
    ],
    perfMetrics: [
      { label: 'App Store Rating', value: '4.8 / 5', score: 96, trend: 'up' },
      { label: 'Crash-Free Rate', value: '99.6%', score: 99, trend: 'stable' },
      { label: 'Cold Start Time', value: '0.9s', score: 91, trend: 'up' },
      { label: 'DAU Retention', value: '68%', score: 68, trend: 'up' },
    ],
    feedback: [
      {
        author: 'Priya Menon',
        company: 'Halcyon Ventures',
        role: 'Product Lead',
        rating: 5,
        comment: 'Delivered ahead of schedule with zero critical bugs at launch. The biometric auth flow is seamless — our users love it.',
        date: 'Aug 8, 2026',
        avatar: 'PM',
      },
      {
        author: 'Chris Alderton',
        company: 'Halcyon Ventures',
        role: 'CEO',
        rating: 5,
        comment: 'Best development partner we\'ve worked with. Communication was transparent throughout and the final product exceeded expectations.',
        date: 'Aug 10, 2026',
        avatar: 'CA',
      },
    ],
  },
  {
    id: 'pf-003',
    name: 'BlueStar E-Commerce Portal',
    client: 'BlueStar Retail',
    description:
      'Full-featured e-commerce platform with multi-vendor support, dynamic pricing engine, loyalty rewards, and headless CMS-driven storefront.',
    status: 'In Development',
    category: 'Web Development',
    startDate: 'May 2026',
    launchDate: 'Oct 2026',
    teamSize: 6,
    budget: '$38,500',
    techStack: [
      { name: 'Next.js 15', category: 'frontend' },
      { name: 'Tailwind CSS', category: 'frontend' },
      { name: 'Sanity CMS', category: 'frontend' },
      { name: 'Nest.js', category: 'backend' },
      { name: 'GraphQL', category: 'backend' },
      { name: 'MySQL', category: 'database' },
      { name: 'Elasticsearch', category: 'database' },
      { name: 'Vercel', category: 'cloud' },
      { name: 'Stripe', category: 'backend' },
    ],
    deployEnvs: [
      { name: 'Development', url: 'dev.bluestar-shop.com', status: 'live', lastDeploy: '3 hours ago', version: 'v0.5.2', uptime: '96.1%' },
      { name: 'Staging', url: 'staging.bluestar-shop.com', status: 'live', lastDeploy: '1 day ago', version: 'v0.4.8', uptime: '97.5%' },
    ],
    perfMetrics: [
      { label: 'Lighthouse Score', value: '79/100', score: 79, trend: 'up' },
      { label: 'Avg Load Time', value: '2.1s', score: 65, trend: 'up' },
      { label: 'API Response', value: '340ms', score: 60, trend: 'stable' },
      { label: 'Test Coverage', value: '51%', score: 51, trend: 'up' },
    ],
    feedback: [
      {
        author: 'Sandra Obi',
        company: 'BlueStar Retail',
        role: 'Head of Digital',
        rating: 4,
        comment: 'Great progress so far. The product search with Elasticsearch is blazing fast. Looking forward to the final delivery.',
        date: 'Jul 28, 2026',
        avatar: 'SO',
      },
    ],
  },
  {
    id: 'pf-004',
    name: 'HealthFirst Patient Portal',
    client: 'HealthFirst Medical',
    description:
      'HIPAA-compliant patient portal with appointment scheduling, telemedicine video calls, lab results viewer, and prescription management.',
    status: 'Testing',
    category: 'Web Development',
    startDate: 'Mar 2026',
    launchDate: 'Sep 2026',
    teamSize: 7,
    budget: '$67,000',
    techStack: [
      { name: 'React 19', category: 'frontend' },
      { name: 'TypeScript', category: 'frontend' },
      { name: 'Tailwind CSS', category: 'frontend' },
      { name: 'Django', category: 'backend' },
      { name: 'Python', category: 'backend' },
      { name: 'PostgreSQL', category: 'database' },
      { name: 'AWS S3', category: 'cloud' },
      { name: 'AWS RDS', category: 'cloud' },
      { name: 'Terraform', category: 'devops' },
      { name: 'Jenkins', category: 'devops' },
    ],
    deployEnvs: [
      { name: 'Production', url: 'portal.healthfirst.com', status: 'maintenance', lastDeploy: '4 days ago', version: 'v1.0.0-rc2', uptime: '99.5%' },
      { name: 'UAT', url: 'uat.portal.healthfirst.com', status: 'live', lastDeploy: '12 hours ago', version: 'v1.0.0-rc3', uptime: '98.9%' },
    ],
    perfMetrics: [
      { label: 'Lighthouse Score', value: '91/100', score: 91, trend: 'up' },
      { label: 'Avg Load Time', value: '1.1s', score: 93, trend: 'up' },
      { label: 'API Response', value: '180ms', score: 88, trend: 'stable' },
      { label: 'Test Coverage', value: '88%', score: 88, trend: 'up' },
    ],
    feedback: [
      {
        author: 'Dr. Amara Osei',
        company: 'HealthFirst Medical',
        role: 'Chief Medical Officer',
        rating: 5,
        comment: 'The telemedicine module is exactly what our patients needed. Security and HIPAA compliance were handled with exceptional care.',
        date: 'Aug 5, 2026',
        avatar: 'AO',
      },
    ],
  },
  {
    id: 'pf-005',
    name: 'Apex Logistics TMS',
    client: 'Apex Logistics',
    description:
      'Custom transportation management system with route optimization, real-time GPS tracking, driver app, and automated dispatch workflows.',
    status: 'In Development',
    category: 'Custom Software',
    startDate: 'Apr 2026',
    launchDate: 'Dec 2026',
    teamSize: 9,
    budget: '$112,000',
    techStack: [
      { name: 'Vue 3', category: 'frontend' },
      { name: 'TypeScript', category: 'frontend' },
      { name: 'Mapbox GL', category: 'frontend' },
      { name: 'Go', category: 'backend' },
      { name: 'gRPC', category: 'backend' },
      { name: 'TimescaleDB', category: 'database' },
      { name: 'Kafka', category: 'backend' },
      { name: 'Kubernetes', category: 'devops' },
      { name: 'GCP', category: 'cloud' },
      { name: 'Helm', category: 'devops' },
    ],
    deployEnvs: [
      { name: 'Dev Cluster', url: 'dev.apex-tms.internal', status: 'live', lastDeploy: '2 hours ago', version: 'v0.3.1', uptime: '95.8%' },
      { name: 'Integration', url: 'int.apex-tms.internal', status: 'live', lastDeploy: '1 day ago', version: 'v0.2.9', uptime: '97.1%' },
    ],
    perfMetrics: [
      { label: 'Route Calc Speed', value: '320ms', score: 72, trend: 'up' },
      { label: 'GPS Update Rate', value: '2s', score: 80, trend: 'stable' },
      { label: 'API Response', value: '95ms', score: 94, trend: 'up' },
      { label: 'Test Coverage', value: '38%', score: 38, trend: 'up' },
    ],
    feedback: [
      {
        author: 'Marcus Delgado',
        company: 'Apex Logistics',
        role: 'VP Operations',
        rating: 4,
        comment: 'The route optimization engine is already showing 18% fuel savings in our pilot tests. Excited for the full rollout.',
        date: 'Jul 30, 2026',
        avatar: 'MD',
      },
    ],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const statusConfig: Record<PortfolioProject['status'], { color: string; icon: React.ElementType; bg: string }> = {
  'In Development': { color: 'text-blue-400', icon: Code2, bg: 'bg-blue-500/10 border-blue-500/20' },
  Testing: { color: 'text-amber-400', icon: AlertTriangle, bg: 'bg-amber-500/10 border-amber-500/20' },
  Deployed: { color: 'text-emerald-400', icon: CheckCircle2, bg: 'bg-emerald-500/10 border-emerald-500/20' },
  Maintenance: { color: 'text-purple-400', icon: Clock, bg: 'bg-purple-500/10 border-purple-500/20' },
  Completed: { color: 'text-teal-400', icon: CheckCircle2, bg: 'bg-teal-500/10 border-teal-500/20' },
};

const envStatusConfig: Record<DeployEnv['status'], { color: string; dot: string; label: string }> = {
  live: { color: 'text-emerald-400', dot: 'bg-emerald-400', label: 'Live' },
  staging: { color: 'text-amber-400', dot: 'bg-amber-400', label: 'Staging' },
  down: { color: 'text-red-400', dot: 'bg-red-400', label: 'Down' },
  maintenance: { color: 'text-purple-400', dot: 'bg-purple-400', label: 'Maintenance' },
};

const techCategoryColor: Record<TechBadge['category'], string> = {
  frontend: 'bg-blue-500/10 text-blue-300 border-blue-500/20',
  backend: 'bg-orange-500/10 text-orange-300 border-orange-500/20',
  database: 'bg-green-500/10 text-green-300 border-green-500/20',
  devops: 'bg-purple-500/10 text-purple-300 border-purple-500/20',
  cloud: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20',
};

const techCategoryIcon: Record<TechBadge['category'], React.ElementType> = {
  frontend: Layers,
  backend: Cpu,
  database: Database,
  devops: GitBranch,
  cloud: Cloud,
};

function ScoreBar({ score, color }: { score: number; color: string }) {
  return (
    <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-700 ${color}`}
        style={{ width: `${score}%` }}
      />
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={13}
          className={s <= rating ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/30'}
        />
      ))}
    </div>
  );
}

function scoreColor(score: number) {
  if (score >= 85) return 'bg-emerald-500';
  if (score >= 65) return 'bg-amber-500';
  return 'bg-red-500';
}

// ─── Project Card ─────────────────────────────────────────────────────────────

function ProjectCard({ project }: { project: PortfolioProject }) {
  const [expanded, setExpanded] = useState(false);
  const statusCfg = statusConfig[project.status];
  const StatusIcon = statusCfg.icon;

  const avgScore = Math.round(
    project.perfMetrics.reduce((a, m) => a + m.score, 0) / project.perfMetrics.length
  );

  return (
    <div className="card-surface rounded-xl overflow-hidden border border-border hover:border-border/80 transition-all duration-200">
      {/* Header */}
      <div className="p-5 pb-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${statusCfg.bg} ${statusCfg.color}`}>
                <StatusIcon size={11} />
                {project.status}
              </span>
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{project.category}</span>
            </div>
            <h3 className="text-base font-semibold text-foreground truncate">{project.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{project.client}</p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <div className="text-right">
              <div className="text-lg font-bold text-foreground">{avgScore}</div>
              <div className="text-2xs text-muted-foreground">Perf Score</div>
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-4">{project.description}</p>

        {/* Quick stats row */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[
            { icon: Users, label: 'Team', value: `${project.teamSize} devs` },
            { icon: BarChart2, label: 'Budget', value: project.budget },
            { icon: Clock, label: 'Start', value: project.startDate },
            { icon: Zap, label: 'Launch', value: project.launchDate },
          ].map((stat) => (
            <div key={stat.label} className="bg-muted/30 rounded-lg p-2.5 text-center">
              <stat.icon size={13} className="text-muted-foreground mx-auto mb-1" />
              <div className="text-xs font-semibold text-foreground">{stat.value}</div>
              <div className="text-2xs text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Tech Stack preview */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {project.techStack.slice(0, 6).map((tech) => (
            <span
              key={tech.name}
              className={`text-2xs font-medium px-2 py-0.5 rounded-full border ${techCategoryColor[tech.category]}`}
            >
              {tech.name}
            </span>
          ))}
          {project.techStack.length > 6 && (
            <span className="text-2xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
              +{project.techStack.length - 6} more
            </span>
          )}
        </div>

        {/* Perf metrics mini bars */}
        <div className="grid grid-cols-2 gap-2">
          {project.perfMetrics.map((m) => (
            <div key={m.label}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-2xs text-muted-foreground">{m.label}</span>
                <span className="text-2xs font-semibold text-foreground">{m.value}</span>
              </div>
              <ScoreBar score={m.score} color={scoreColor(m.score)} />
            </div>
          ))}
        </div>
      </div>

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-3 border-t border-border bg-muted/10 hover:bg-muted/20 transition-colors text-xs text-muted-foreground hover:text-foreground"
      >
        <span>{expanded ? 'Hide details' : 'View tech stack, deployments & feedback'}</span>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-border">
          {/* Full Tech Stack */}
          <div className="p-5 border-b border-border">
            <div className="flex items-center gap-2 mb-3">
              <Package size={14} className="text-primary" />
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">Full Tech Stack</h4>
            </div>
            <div className="space-y-3">
              {(['frontend', 'backend', 'database', 'devops', 'cloud'] as TechBadge['category'][]).map((cat) => {
                const items = project.techStack.filter((t) => t.category === cat);
                if (!items.length) return null;
                const CatIcon = techCategoryIcon[cat];
                return (
                  <div key={cat} className="flex items-start gap-3">
                    <div className="flex items-center gap-1.5 w-24 flex-shrink-0 mt-0.5">
                      <CatIcon size={12} className="text-muted-foreground" />
                      <span className="text-2xs text-muted-foreground capitalize">{cat}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {items.map((t) => (
                        <span key={t.name} className={`text-2xs font-medium px-2 py-0.5 rounded-full border ${techCategoryColor[t.category]}`}>
                          {t.name}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Deployment Environments */}
          <div className="p-5 border-b border-border">
            <div className="flex items-center gap-2 mb-3">
              <Server size={14} className="text-primary" />
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">Deployment Environments</h4>
            </div>
            <div className="space-y-2">
              {project.deployEnvs.map((env) => {
                const envCfg = envStatusConfig[env.status];
                return (
                  <div key={env.name} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border/50">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${envCfg.dot} ${env.status === 'live' ? 'animate-pulse' : ''}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-foreground">{env.name}</span>
                        <span className={`text-2xs font-medium ${envCfg.color}`}>{envCfg.label}</span>
                        <span className="text-2xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{env.version}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-2xs text-muted-foreground truncate">{env.url}</span>
                        <span className="text-2xs text-muted-foreground flex-shrink-0">↑ {env.uptime}</span>
                        <span className="text-2xs text-muted-foreground flex-shrink-0">Deployed {env.lastDeploy}</span>
                      </div>
                    </div>
                    {project.liveUrl && env.status === 'live' && (
                      <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground flex-shrink-0">
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="p-5 border-b border-border">
            <div className="flex items-center gap-2 mb-3">
              <Gauge size={14} className="text-primary" />
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">Performance Metrics</h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {project.perfMetrics.map((m) => (
                <div key={m.label} className="p-3 rounded-lg bg-muted/20 border border-border/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">{m.label}</span>
                    <div className="flex items-center gap-1">
                      <TrendingUp
                        size={11}
                        className={m.trend === 'up' ? 'text-emerald-400' : m.trend === 'down' ? 'text-red-400 rotate-180' : 'text-muted-foreground'}
                      />
                    </div>
                  </div>
                  <div className="text-sm font-bold text-foreground mb-2">{m.value}</div>
                  <ScoreBar score={m.score} color={scoreColor(m.score)} />
                  <div className="text-right mt-1">
                    <span className={`text-2xs font-semibold ${m.score >= 85 ? 'text-emerald-400' : m.score >= 65 ? 'text-amber-400' : 'text-red-400'}`}>
                      {m.score}/100
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Client Feedback */}
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare size={14} className="text-primary" />
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">Client Feedback</h4>
            </div>
            <div className="space-y-3">
              {project.feedback.map((fb, i) => (
                <div key={i} className="p-4 rounded-lg bg-muted/20 border border-border/50">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-primary">{fb.avatar}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div>
                          <span className="text-xs font-semibold text-foreground">{fb.author}</span>
                          <span className="text-2xs text-muted-foreground ml-1.5">— {fb.role}, {fb.company}</span>
                        </div>
                        <span className="text-2xs text-muted-foreground flex-shrink-0">{fb.date}</span>
                      </div>
                      <StarRating rating={fb.rating} />
                      <p className="text-xs text-muted-foreground leading-relaxed mt-2">&ldquo;{fb.comment}&rdquo;</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const STATUS_FILTERS = ['All', 'In Development', 'Testing', 'Deployed', 'Maintenance', 'Completed'] as const;
const CATEGORY_FILTERS = ['All', 'Web Development', 'Mobile App', 'ERP / CRM', 'Custom Software'];

export default function PortfolioPage() {
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [categoryFilter, setCategoryFilter] = useState<string>('All');

  const filtered = portfolioProjects.filter((p) => {
    const matchStatus = statusFilter === 'All' || p.status === statusFilter;
    const matchCat = categoryFilter === 'All' || p.category === categoryFilter;
    return matchStatus && matchCat;
  });

  // Summary stats
  const totalProjects = portfolioProjects.length;
  const deployed = portfolioProjects.filter((p) => p.status === 'Deployed' || p.status === 'Completed').length;
  const inDev = portfolioProjects.filter((p) => p.status === 'In Development').length;
  const avgRating = (
    portfolioProjects.flatMap((p) => p.feedback).reduce((a, f) => a + f.rating, 0) /
    portfolioProjects.flatMap((p) => p.feedback).length
  ).toFixed(1);

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-6 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Globe size={16} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Project Portfolio</h1>
              <p className="text-xs text-muted-foreground">Development status, tech stack, deployments, performance & client feedback</p>
            </div>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { icon: Layers, label: 'Total Projects', value: totalProjects, color: 'text-blue-400', bg: 'bg-blue-500/10' },
            { icon: CheckCircle2, label: 'Live / Completed', value: deployed, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
            { icon: Code2, label: 'In Development', value: inDev, color: 'text-amber-400', bg: 'bg-amber-500/10' },
            { icon: Star, label: 'Avg Client Rating', value: `${avgRating}/5`, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
          ].map((kpi) => (
            <div key={kpi.label} className="card-surface rounded-xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${kpi.bg} flex items-center justify-center flex-shrink-0`}>
                <kpi.icon size={18} className={kpi.color} />
              </div>
              <div>
                <div className="text-xl font-bold text-foreground">{kpi.value}</div>
                <div className="text-xs text-muted-foreground">{kpi.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="card-surface rounded-xl p-4 mb-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-muted-foreground">Status:</span>
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                  statusFilter === s
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted/30 text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:ml-4">
            <span className="text-xs font-medium text-muted-foreground">Category:</span>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-input border border-border text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            >
              {CATEGORY_FILTERS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="sm:ml-auto text-xs text-muted-foreground">
            {filtered.length} of {totalProjects} projects
          </div>
        </div>

        {/* Project Grid */}
        {filtered.length === 0 ? (
          <div className="card-surface rounded-xl p-16 text-center">
            <Activity size={32} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-medium text-foreground">No projects match your filters</p>
            <p className="text-xs text-muted-foreground mt-1">Try adjusting the status or category filter</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
